import type { RemoteConfigProvider } from './provider';

/**
 * Opções para inicialização do Remote Config Service
 */
export interface RemoteConfigServiceOptions {
  /** Valores padrão para configurações */
  defaultValues?: Record<string, string | number | boolean>;
  /** Opções específicas do provider */
  providerOptions?: Record<string, any>;
}

/**
 * Serviço de Remote Config completamente agnóstico
 *
 * Este serviço:
 * - Não sabe quais configurações existem
 * - Orquestra um provider registrado
 * - Fornece fallback para valores padrão
 * - Trabalha com valores genéricos (string, number, boolean)
 *
 * Cada aplicação define suas próprias feature flags e cria um adapter/wrapper
 * que usa este serviço genérico.
 */
class RemoteConfigService {
  private provider: RemoteConfigProvider | null = null;
  private defaultValues: Record<string, string | number | boolean> = {};

  /**
   * Registra um provider de Remote Config
   * @param provider - Implementação do provider (Firebase, API própria, etc.)
   */
  registerProvider(provider: RemoteConfigProvider): void {
    if (this.provider) {
      console.warn('[REMOTE_CONFIG] Provider already registered, replacing...');
    }
    this.provider = provider;
    console.log('[REMOTE_CONFIG] Provider registered successfully');
  }

  /**
   * Inicializa o Remote Config com o provider registrado
   * @param options - Opções de configuração
   */
  async initialize(options: RemoteConfigServiceOptions = {}): Promise<void> {
    if (options.defaultValues) {
      this.defaultValues = options.defaultValues;
    }

    if (!this.provider) {
      console.warn('[REMOTE_CONFIG] No provider registered, using only default values');
      return;
    }

    try {
      await this.provider.initialize(options.providerOptions);
      console.log('[REMOTE_CONFIG] Initialized successfully with provider');
    } catch (error) {
      console.error('[REMOTE_CONFIG] Failed to initialize provider:', error);
    }
  }

  /**
   * Retorna um valor numérico
   * @param key - Chave da configuração
   * @param defaultValue - Valor padrão se não encontrado
   */
  getNumber(key: string, defaultValue?: number): number | undefined {
    const value = this.provider?.getNumber(key);
    if (value !== undefined) return value;
    if (this.defaultValues[key] !== undefined && typeof this.defaultValues[key] === 'number') {
      return this.defaultValues[key] as number;
    }
    return defaultValue;
  }

  /**
   * Retorna um valor booleano
   * @param key - Chave da configuração
   * @param defaultValue - Valor padrão se não encontrado
   */
  getBoolean(key: string, defaultValue?: boolean): boolean | undefined {
    const value = this.provider?.getBoolean(key);
    if (value !== undefined) return value;
    if (this.defaultValues[key] !== undefined && typeof this.defaultValues[key] === 'boolean') {
      return this.defaultValues[key] as boolean;
    }
    return defaultValue;
  }

  /**
   * Retorna um valor string
   * @param key - Chave da configuração
   * @param defaultValue - Valor padrão se não encontrado
   */
  getString(key: string, defaultValue?: string): string | undefined {
    const value = this.provider?.getString(key);
    if (value !== undefined) return value;
    if (this.defaultValues[key] !== undefined && typeof this.defaultValues[key] === 'string') {
      return this.defaultValues[key] as string;
    }
    return defaultValue;
  }

  /**
   * Força atualização das configurações remotas
   */
  async refresh(): Promise<void> {
    if (!this.provider) {
      console.warn('[REMOTE_CONFIG] No provider registered, cannot refresh');
      return;
    }

    try {
      await this.provider.refresh();
      console.log('[REMOTE_CONFIG] Config refreshed');
    } catch (error) {
      console.error('[REMOTE_CONFIG] Error refreshing config:', error);
    }
  }

  /**
   * Verifica se o serviço foi inicializado
   */
  isInitialized(): boolean {
    return this.provider?.isInitialized() ?? false;
  }

  /**
   * Verifica se há um provider registrado
   */
  hasProvider(): boolean {
    return this.provider !== null;
  }

  /**
   * Reseta o serviço (útil para testes)
   */
  reset(): void {
    this.provider = null;
    this.defaultValues = {};
  }
}

// Singleton instance
export const remoteConfigService = new RemoteConfigService();
